/**
 * Narrative Display Component
 * Displays character dialogues and story narratives
 * Enhanced to support multi-paragraph narratives with speaker badges and video
 */

import React, { useState, useEffect } from 'react';
import { Narrative, CharacterMessage } from '../content/ContentLoader';
import { contentLoader } from '../content';
import { getCharacterIcon } from '@/config/playerIcons';
import { IoChatbubble } from 'react-icons/io5';
import VideoPlayer from './VideoPlayer';

interface NarrativeDisplayProps {
  narrative?: Narrative;
  messages?: CharacterMessage[];
  className?: string;
  showSpeakerBadge?: boolean;
  onVideoEnded?: () => void;
}

export const NarrativeDisplay: React.FC<NarrativeDisplayProps> = ({
  narrative,
  messages,
  className = '',
  showSpeakerBadge = true,
  onVideoEnded
}) => {
  const [videoFinished, setVideoFinished] = useState(false);
  const [showText, setShowText] = useState(false);

  // Reset video and text state when narrative changes
  useEffect(() => {
    setVideoFinished(false);
    setShowText(false);
  }, [narrative?.videoUrl]);

  const getVoiceIcon = (voice: string): React.ReactNode => {
    const iconUrl = getCharacterIcon(voice);
    if (iconUrl) {
      return <img src={iconUrl} alt={voice} className="w-8 h-8 object-contain" />;
    }
    return <IoChatbubble />;
  };

  const handleVideoEnded = () => {
    setVideoFinished(true);
    onVideoEnded?.();
  };

  if (narrative) {
    const color = contentLoader.getVoiceColor(narrative.voice);
    const icon = getVoiceIcon(narrative.voice);
    const paragraphs = contentLoader.formatNarrative(narrative.text);
    const hasVideo = !!narrative.videoUrl;
    const isVideoOnlyPlaying = hasVideo && narrative.videoOnly && !videoFinished;
    const textVisible = !isVideoOnlyPlaying || showText;

    return (
      <div className={`bg-white border-l-4 border-gray-200 rounded-xl shadow-sm overflow-hidden ${className}`} style={{ borderLeftColor: color }}>
        {showSpeakerBadge && (
          <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
            <span className="text-2xl">{icon}</span>
            <span className="text-lg font-semibold" style={{ color }}>
              {narrative.voice}
            </span>
          </div>
        )}

        {/* Video Player */}
        {hasVideo && !videoFinished && (
          <div className="px-4 pt-4">
            <VideoPlayer
              src={narrative.videoUrl!}
              onEnded={handleVideoEnded}
              autoPlay
            />
          </div>
        )}

        {/* Show Text toggle for video-only narratives */}
        {isVideoOnlyPlaying && (
          <div className="px-6 pt-3">
            <button
              onClick={() => setShowText(!showText)}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1.5"
            >
              <svg
                className={`w-4 h-4 transition-transform ${showText ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              {showText ? 'Hide narration text' : 'Show narration text'}
            </button>
          </div>
        )}

        {/* Text Content */}
        {textVisible && (
          <div className={`${showSpeakerBadge || hasVideo ? 'px-6 py-5' : 'p-6'} space-y-4`}>
            {paragraphs.map((para, idx) => (
              <p key={idx} className="text-gray-800 text-base leading-relaxed">
                {para}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (messages && messages.length > 0) {
    return (
      <div className={`space-y-4 ${className}`}>
        {messages.map((msg, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="font-semibold text-black mb-2">
              {msg.from}:
            </div>
            <div className="text-gray-700">{msg.text}</div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export default NarrativeDisplay;
